import { Box, Image, Text } from '@chakra-ui/react';
import { ResponsiveValue } from '@chakra-ui/react';
import { Property } from "csstype";
import { AutomationTarget } from '../hooks/useGetOrderWorkflowStatus';
import YaxleLogo from '../assets/yaxleLogoBlue.png';
import { brandCopy } from '../constants/brand';

type SourceBadgeType = AutomationTarget | 'ccc-sp' | 'inventory';

interface SourceBadgeProps {
    source: SourceBadgeType;
}

const SOURCE_CONFIG: Record<Exclude<SourceBadgeType, 'inventory'>, { bg: ResponsiveValue<Property.Color>; label: string; color?: ResponsiveValue<Property.Color>; }> = {
    ccc: { bg: 'blue.300', label: 'C' },
    'ccc-sp': { bg: 'blue.300', label: 'SP' },
    tesla: { bg: 'red.500', label: 'T' },
    opstrax: { bg: 'gray.400', label: 'O' },
    mitchell: { bg: 'orange.400', label: 'M' },
    partstrader: { bg: 'blue.600', label: 'PT' },
    collisionlink: { bg: '#C81F3F', label: 'CL' },
};

/**
 * Hex equivalents of the SourceBadge Chakra tokens plus full marketplace
 * labels. Used by charts (e.g. recharts) that need literal color values
 * rather than theme tokens. Keep in sync with SOURCE_CONFIG above.
 */
export const SOURCE_MARKETPLACE_CONFIG: Record<string, { color: string; label: string }> = {
    ccc: { color: '#63B3ED', label: 'CCC' },          // blue.300
    'ccc-sp': { color: '#4299E1', label: 'CCC SP' },  // blue.400 (distinct from CCC)
    tesla: { color: '#E53E3E', label: 'Tesla' },      // red.500
    opstrax: { color: '#A0AEC0', label: 'OPSTrax' },  // gray.400
    mitchell: { color: '#ED8936', label: 'Mitchell' },// orange.400
    partstrader: { color: '#2B6CB0', label: 'PartsTrader' }, // blue.600
    collisionlink: { color: '#C81F3F', label: 'CollisionLink' },
    inventory: { color: '#1A365D', label: 'In Stock' },
    unknown: { color: '#CBD5E0', label: 'Other' },    // gray.300
};

const SOURCE_FALLBACK_COLORS = ['#805AD5', '#38A169', '#D69E2E', '#DD6B20', '#319795', '#E53E3E'];

export function getSourceColor(source: string, index = 0): string {
    return SOURCE_MARKETPLACE_CONFIG[source]?.color
        ?? SOURCE_FALLBACK_COLORS[index % SOURCE_FALLBACK_COLORS.length];
}

export function getSourceLabel(source: string): string {
    return SOURCE_MARKETPLACE_CONFIG[source]?.label
        ?? source.charAt(0).toUpperCase() + source.slice(1);
}

export type ActivitySourceBadgeType = AutomationTarget | 'ccc-sp';

/**
 * Map raw backend-emitted source values to the canonical source keys used by
 * SourceBadge. Activity log metadata historically used short or display codes
 * (e.g. `OT`, `CCC`, `PartsTrader`). Returns null for values SourceBadge doesn't
 * know about (e.g. `Manual`) so the caller can fall back to plain text.
 */
export function toSourceBadgeType(src: string): ActivitySourceBadgeType | null {
    switch (src) {
        case 'OT':
        case 'OpsTrax':
        case 'opstrax':
            return 'opstrax';
        case 'CCC':
        case 'ccc':
            return 'ccc';
        case 'CCC-SP':
        case 'ccc-sp':
            return 'ccc-sp';
        case 'PT':
        case 'pt':
        case 'PartsTrader':
        case 'partstrader':
            return 'partstrader';
        case 'Tesla':
        case 'tesla':
            return 'tesla';
        case 'Mitchell':
        case 'mitchell':
            return 'mitchell';
        case 'CollisionLink':
        case 'collisionlink':
            return 'collisionlink';
        default:
            return null;
    }
}

/** Marketplace display name for any activity-log source value, including short codes. */
export function getActivitySourceLabel(src: string): string {
    return getSourceLabel(toSourceBadgeType(src) ?? src);
}

/** Text rendered inside the SourceBadge square (e.g. `PT`, `CL`). */
export function getSourceBadgeLabel(source: ActivitySourceBadgeType): string {
    return (SOURCE_CONFIG[source] ?? SOURCE_CONFIG.partstrader).label;
}

const SourceBadge = ({ source }: SourceBadgeProps) => {
    if (source === 'inventory') {
        return (
            <Box
                w="24px"
                h="24px"
                borderRadius="md"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                overflow="hidden"
            >
                <Image src={YaxleLogo} alt={brandCopy('PartBay In Stock')} w="24px" h="24px" objectFit="contain" />
            </Box>
        );
    }

    const config = SOURCE_CONFIG[source] ?? SOURCE_CONFIG.partstrader;

    return (
        <Box
            w="24px"
            h="24px"
            borderRadius="md"
            bg={config.bg}
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
        >
            <Text fontSize="xs" color={config.color ?? 'white'} fontWeight="bold">
                {config.label}
            </Text>
        </Box>
    );
};

export default SourceBadge;
